import { useRef, useState, useEffect, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { computeCatalogNamePrefix } from '../namingConvention';
import { parseWorkspaceName } from '../DatabricksWorkspaceField/DatabricksWorkspaceField';
import {
  catalogNameConflictMessage,
  findCatalogNameConflicts,
  setKnownCatalogState,
  type CatalogLookupStatus,
} from './catalogNameConflict';

interface CatalogEntry {
  name: string;
  comment?: string;
  schemas: string[];
}

const NAME_PATTERN = /^[a-z][a-z0-9_]{1,62}$/;
const INVALID_SCHEMA_MESSAGE =
  'Invalid schema name. Use lowercase letters, numbers, and underscores; ' +
  'start with a letter and use 2 to 63 characters.';

const TEMPLATE_JSON = JSON.stringify(
  {
    catalogs: [
      {
        name: '<catalog_name, e.g. sales_domain>',
        comment: '<optional description>',
        schemas: ['<schema_name, e.g. bronze>', '<schema_name, e.g. silver>'],
      },
    ],
  },
  null,
  2,
);

// Validates the shape of an imported file without mutating anything -
// returns either the parsed catalogs or a list of human-readable errors.
function validateImportedCatalogs(
  raw: unknown,
): { catalogs: CatalogEntry[] } | { errors: string[] } {
  const errors: string[] = [];

  if (
    typeof raw !== 'object' ||
    raw === null ||
    !Array.isArray((raw as { catalogs?: unknown }).catalogs)
  ) {
    return {
      errors: ['File must be a JSON object with a top-level "catalogs" array.'],
    };
  }

  const rawCatalogs = (raw as { catalogs: unknown[] }).catalogs;
  const catalogs: CatalogEntry[] = [];

  rawCatalogs.forEach((entry, index) => {
    const label = `catalogs[${index}]`;
    if (typeof entry !== 'object' || entry === null) {
      errors.push(`${label}: must be an object.`);
      return;
    }
    const { name, comment, schemas } = entry as Record<string, unknown>;

    if (typeof name !== 'string' || !NAME_PATTERN.test(name)) {
      errors.push(
        `${label}: "name" must be a lowercase identifier (letters, numbers, underscores).`,
      );
      return;
    }
    if (comment !== undefined && typeof comment !== 'string') {
      errors.push(`${label}: "comment" must be a string if provided.`);
      return;
    }
    if (
      !Array.isArray(schemas) ||
      schemas.length === 0 ||
      !schemas.every(s => typeof s === 'string' && NAME_PATTERN.test(s))
    ) {
      errors.push(
        `${label}: "schemas" must be a non-empty array of lowercase identifiers.`,
      );
      return;
    }

    catalogs.push({ name, comment: comment as string | undefined, schemas });
  });

  return errors.length > 0 ? { errors } : { catalogs };
}

export const CatalogsAndSchemasField = ({
  onChange,
  formData,
  formContext,
}: FieldExtensionComponentProps<CatalogEntry[]>) => {
  const useExistingCatalog =
    formContext?.formData?.add_existing_catalog === true;
  const catalogs = formData ?? [];
  const workspaceSelection = formContext?.formData?.workspace as
    | string
    | { workspaces?: string[]; environments?: string[]; spoke?: string }
    | undefined;
  const selectedWorkspaces =
    typeof workspaceSelection === 'string'
      ? [workspaceSelection]
      : workspaceSelection?.workspaces ?? [];
  const workspaceKey = selectedWorkspaces.join('\u0000');
  const workspaces = useMemo(
    () => (workspaceKey ? workspaceKey.split('\u0000') : []),
    [workspaceKey],
  );
  const prefixes = useMemo(
    () =>
      workspaces.map(workspace => {
        const parsed = parseWorkspaceName(workspace);
        return parsed
          ? `${parsed.spoke.toLowerCase()}_${parsed.environment.toLowerCase()}_`
          : computeCatalogNamePrefix(workspace);
      }),
    [workspaces],
  );
  const workspace = workspaces[0];
  const prefix = prefixes[0] ?? '';

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [existingNames, setExistingNames] = useState<string[]>([]);
  const [lookupStatus, setLookupStatus] = useState<CatalogLookupStatus>('idle');

  // Fetch the catalogs that already exist on the selected workspace so we can
  // block requests for names that are already taken. The Databricks credential
  // stays server-side: the browser only sends the workspace name and receives
  // catalog names back.
  useEffect(() => {
    let cancelled = false;

    if (useExistingCatalog || !workspace) {
      setExistingNames([]);
      setLookupStatus('idle');
      setKnownCatalogState({
        workspace: undefined,
        prefix,
        workspaces: [],
        prefixes: [],
        existingNames: [],
        status: 'idle',
      });
      return () => {
        cancelled = true;
      };
    }

    setLookupStatus('loading');
    setKnownCatalogState({
      workspace,
      prefix,
      workspaces,
      prefixes,
      existingNames: [],
      status: 'loading',
    });

    (async () => {
      try {
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/databricks/catalogs?workspaces=${encodeURIComponent(
            JSON.stringify(workspaces),
          )}`,
        );
        if (!response.ok) {
          throw new Error(`${response.status}`);
        }
        const body = (await response.json()) as {
          catalogs?: Record<string, string[]>;
        };
        const names = Array.from(
          new Set(
            Object.values(body.catalogs ?? {}).flatMap(
              workspaceCatalogs => workspaceCatalogs,
            ),
          ),
        );
        if (!cancelled) {
          setExistingNames(names);
          setLookupStatus('ok');
          setKnownCatalogState({
            workspace,
            prefix,
            workspaces,
            prefixes,
            existingNames: names,
            status: 'ok',
          });
        }
      } catch {
        if (!cancelled) {
          setExistingNames([]);
          setLookupStatus('error');
          setKnownCatalogState({
            workspace,
            prefix,
            workspaces,
            prefixes,
            existingNames: [],
            status: 'error',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    useExistingCatalog,
    workspace,
    workspaceKey,
    workspaces,
    prefix,
    prefixes,
    discoveryApi,
    fetchApi,
  ]);

  const conflictingIndexes = new Map(
    findCatalogNameConflicts(catalogs, prefixes, existingNames).map(c => [
      c.index,
      c.fullName,
    ]),
  );

  const [schemaDrafts, setSchemaDrafts] = useState<Record<number, string>>({});
  const [importMessage, setImportMessage] = useState<{
    type: 'success' | 'error';
    lines: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateCatalog = (index: number, patch: Partial<CatalogEntry>) => {
    onChange(catalogs.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const addCatalog = () => {
    onChange([...catalogs, { name: '', comment: '', schemas: [] }]);
  };

  const removeCatalog = (index: number) => {
    onChange(catalogs.filter((_, i) => i !== index));
  };

  const addSchema = (index: number) => {
    const draft = (schemaDrafts[index] ?? '').trim();
    if (!draft || catalogs[index].schemas.includes(draft)) {
      return;
    }
    updateCatalog(index, { schemas: [...catalogs[index].schemas, draft] });
    setSchemaDrafts(prev => ({ ...prev, [index]: '' }));
  };

  const removeSchema = (index: number, schema: string) => {
    updateCatalog(index, {
      schemas: catalogs[index].schemas.filter(s => s !== schema),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_JSON], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'catalogs-template.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(reader.result));
      } catch {
        setImportMessage({
          type: 'error',
          lines: ['File is not valid JSON.'],
        });
        return;
      }

      const result = validateImportedCatalogs(parsed);
      if ('errors' in result) {
        setImportMessage({ type: 'error', lines: result.errors });
        return;
      }

      const existingCatalogNames = new Set(catalogs.map(c => c.name));
      const toAdd = result.catalogs.filter(
        c => !existingCatalogNames.has(c.name),
      );
      const skipped = result.catalogs.length - toAdd.length;

      onChange([...catalogs, ...toAdd]);
      setImportMessage({
        type: 'success',
        lines: [
          `Imported ${toAdd.length} catalog${toAdd.length === 1 ? '' : 's'}.`,
          ...(skipped > 0
            ? [`Skipped ${skipped} already present by name.`]
            : []),
        ],
      });
    };
    reader.readAsText(file);
  };

  if (useExistingCatalog) {
    return null;
  }

  return (
    <Box>
      <Box display="flex" style={{ gap: 8 }} mb={2}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={downloadTemplate}
        >
          Download template
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PublishIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Import from JSON
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </Box>

      {importMessage && (
        <Box mb={2}>
          {importMessage.lines.map((line, i) => (
            <Typography
              key={i}
              variant="body2"
              color={importMessage.type === 'error' ? 'error' : 'textPrimary'}
              display="block"
            >
              {line}
            </Typography>
          ))}
        </Box>
      )}
      {workspace && lookupStatus === 'error' && (
        <Box mb={2}>
          <Typography variant="body2" color="error" display="block">
            Could not check existing catalogs on this workspace. Requests are
            blocked until the check succeeds — please retry, or reselect the
            workspace.
          </Typography>
        </Box>
      )}

      {catalogs.map((catalog, index) => {
        const nameIsValid = !catalog.name || NAME_PATTERN.test(catalog.name);
        const conflictFullName = conflictingIndexes.get(index);
        return (
          <Paper
            key={index}
            variant="outlined"
            style={{ padding: 16, marginBottom: 16 }}
          >
            <Box display="flex" alignItems="flex-start">
              <Box flexGrow={1} mr={1}>
                <TextField
                  fullWidth
                  size="small"
                  margin="dense"
                  label="Catalog identifier"
                  id={`catalog-name-${index}`}
                  placeholder="e.g. sales_domain"
                  value={catalog.name}
                  error={!nameIsValid || Boolean(conflictFullName)}
                  helperText={(() => {
                    if (conflictFullName) {
                      return catalogNameConflictMessage(conflictFullName);
                    }
                    if (nameIsValid) {
                      return 'Short business name only - lowercase letters, numbers, underscores';
                    }
                    return 'Must be lowercase, start with a letter, and use only letters/numbers/underscores';
                  })()}
                  onChange={e => updateCatalog(index, { name: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  margin="dense"
                  label="Description (optional)"
                  id={`catalog-comment-${index}`}
                  value={catalog.comment ?? ''}
                  onChange={e =>
                    updateCatalog(index, { comment: e.target.value })
                  }
                />
              </Box>
              <IconButton
                aria-label={`Remove catalog ${index + 1}`}
                onClick={() => removeCatalog(index)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {catalog.name && nameIsValid && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ fontFamily: 'monospace', display: 'block' }}
              >
                {prefixes.length === 1 ? (
                  <>
                    Will be created as: {prefixes[0]}
                    {catalog.name}
                  </>
                ) : (
                  <>
                    Will be created as:
                    {prefixes.map(environmentPrefix => (
                      <span
                        key={environmentPrefix}
                        style={{ display: 'block' }}
                      >
                        {environmentPrefix}
                        {catalog.name}
                      </span>
                    ))}
                  </>
                )}
              </Typography>
            )}

            <Divider style={{ margin: '12px 0' }} />

            <Typography variant="body2" gutterBottom>
              Schemas
            </Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mb={1}>
              {catalog.schemas.length === 0 && (
                <Typography variant="caption" color="error">
                  At least one schema is required
                </Typography>
              )}
              {catalog.schemas.map(schema => (
                <Box key={schema}>
                  <Chip
                    label={schema}
                    size="small"
                    color={NAME_PATTERN.test(schema) ? 'default' : 'secondary'}
                    onDelete={() => removeSchema(index, schema)}
                  />
                  {!NAME_PATTERN.test(schema) && (
                    <Typography variant="caption" color="error" display="block">
                      {INVALID_SCHEMA_MESSAGE}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            <Box display="flex" alignItems="center">
              <TextField
                size="small"
                label="Schema name"
                id={`catalog-schema-input-${index}`}
                placeholder="Schema name, e.g. bronze"
                value={schemaDrafts[index] ?? ''}
                onChange={e =>
                  setSchemaDrafts(prev => ({
                    ...prev,
                    [index]: e.target.value,
                  }))
                }
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSchema(index);
                  }
                }}
              />
              <Button
                size="small"
                onClick={() => addSchema(index)}
                startIcon={<AddIcon />}
              >
                Add schema
              </Button>
            </Box>
          </Paper>
        );
      })}

      <Button variant="outlined" onClick={addCatalog} startIcon={<AddIcon />}>
        Add catalog
      </Button>
    </Box>
  );
};
